import React, { useEffect, useRef, useState } from "react";
import AccountLayout from "../../account/AccountLayout";
import { fetchDeletionReceipt, fetchDeletionStatus } from "../../account/accountApiClient";
import { AccountButton, AccountCard, AccountStatus } from "../../account/AccountUi";
import { readDeletionReceipt } from "../../account/deletionStorage";
import { useAuth, AUTH_PHASES } from "../../auth/AuthProvider";

const TERMINAL = new Set(["completed", "failed", "needs_operator", "partial"]);

export default function DeletionStatusPage() {
  return (
    <AccountLayout
      title="Deletion status"
      description="Status of your Stratify account deletion request."
      path="/account/deletion-status"
      allowClosing
      allowAnonymousReceipt
    >
      <DeletionStatusBody />
    </AccountLayout>
  );
}

function DeletionStatusBody() {
  const auth = useAuth();
  const stored = readDeletionReceipt();
  const [job, setJob] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [state, setState] = useState("idle");
  const [error, setError] = useState(null);
  const terminalRef = useRef(false);

  async function load() {
    setState("loading");
    setError(null);
    try {
      let status = "";
      if (auth.signedIn && stored.jobId) {
        const json = await fetchDeletionStatus(stored.jobId);
        setJob(json);
        status = String(json?.status || "").toLowerCase();
        setState("ready");
      } else if (stored.deletionCapability) {
        const json = await fetchDeletionReceipt(stored.deletionCapability);
        setReceipt(json);
        status = String(json?.status || json?.phase || "").toLowerCase();
        setState("ready");
      } else {
        setState("empty");
      }
      if (TERMINAL.has(status)) terminalRef.current = true;
    } catch (err) {
      setError(err);
      setState("error");
    }
  }

  useEffect(() => {
    let cancelled = false;
    terminalRef.current = false;
    const run = async () => {
      if (!cancelled) await load();
    };
    run();
    const id = window.setInterval(() => {
      if (!terminalRef.current) run();
    }, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.signedIn, stored.jobId, stored.deletionCapability]);

  const status = String(job?.status || receipt?.status || receipt?.phase || "").toLowerCase();
  const dryRunNote =
    "If processing has not started, the request is accepted and pending. The website does not delete data in the browser.";

  let headline = "Deletion requested — pending processing";
  let detail = dryRunNote;
  if (status === "completed") {
    headline = "Account deletion completed";
    detail = "This account is closed.";
  } else if (status === "needs_operator") {
    headline = "Deletion needs operator attention";
    detail = job?.blockerCode
      ? `A provider step is blocked (${job.blockerCode}). Contact support with your account email.`
      : "A provider step is blocked. Contact support with your account email.";
  } else if (status === "failed") {
    headline = "Deletion did not finish";
    detail = "Contact support. Do not retry from a different device with a new key; this page replays the same request.";
  } else if (status === "partial") {
    headline = "Deletion partially finished";
    detail = "Some retained or failed steps remain. Contact support.";
  } else if (auth.phase === AUTH_PHASES.deletionRequested) {
    headline = "This account is closing";
    detail = dryRunNote;
  }

  return (
      <AccountCard>
        {state === "loading" && !job && !receipt ? <AccountStatus>Checking deletion status…</AccountStatus> : null}
        {state === "empty" ? (
          <AccountStatus>No deletion request was found in this browser.</AccountStatus>
        ) : (
          <>
            <p className="text-lg font-semibold text-ink">{headline}</p>
            <p className="mt-2 text-sm text-muted">{detail}</p>
            {status ? <p className="mt-3 text-xs uppercase tracking-wide text-muted">Status: {status}</p> : null}
          </>
        )}
        {state === "error" ? (
          <AccountStatus tone="error">{error?.message || "Could not load deletion status."}</AccountStatus>
        ) : null}
        <AccountButton variant="secondary" className="mt-4" onClick={load}>
          Refresh
        </AccountButton>
      </AccountCard>
  );
}
